/* PB Search Drawer
   - Custom element <pb-search-drawer> with .open() / .close()
   - Wires up the header search icon (replaces Dawn's predictive-search modal trigger)
   - Live results from Shopify's /search/suggest.json when typing
   - Empty input → original featured product blocks shown
   - Submits the form to /search?q=… on Enter (full Shopify search page)
   - Closes on overlay click, ✕ button, ESC key
*/
(function () {
  'use strict';

  if (customElements.get('pb-search-drawer')) return;

  var DEBOUNCE_MS = 250;
  var SUGGEST_URL = '/search/suggest.json';
  var FETCH_LIMIT = 8;

  function moneyFormat(cents) {
    if (typeof cents !== 'number') return '';
    var dollars = cents / 100;
    return '$' + dollars.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // PB:NEW 2026-06-08 (QA S-02) — /search/suggest.json returns `price` as a
  // string in DOLLARS with no currency symbol (e.g. "69.97"). Normalise any
  // price value to a "$xx.xx" display string so the symbol is never missing.
  function formatPrice(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'number') return moneyFormat(value);
    var str = String(value).trim();
    if (!str) return '';
    // Already symbol-prefixed (some locales) → leave as-is.
    if (/[^\d.,\s]/.test(str.charAt(0))) return str;
    return '$' + str;
  }

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  class PbSearchDrawer extends HTMLElement {
    constructor() {
      super();
      this.lastFocus = null;
      this.featuredHtml = '';
      this.fetchTimer = null;
      this.lastQuery = '';
      this.abortCtl = null;
    }

    connectedCallback() {
      this.overlay = this.querySelector('[data-pb-search-overlay]');
      this.closeBtn = this.querySelector('[data-pb-search-close]');
      this.input = this.querySelector('[data-pb-search-input]');
      this.clearBtn = this.querySelector('[data-pb-search-clear]');
      this.form = this.querySelector('[data-pb-search-form]');
      this.productsEl = this.querySelector('[data-pb-search-products]');
      this.noResults = this.querySelector('[data-pb-search-no-results]');

      // Cache the server-rendered featured products so we can restore
      // them whenever the input is cleared.
      if (this.productsEl) this.featuredHtml = this.productsEl.innerHTML;

      if (this.overlay)  this.overlay.addEventListener('click', () => this.close());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.input)    this.input.addEventListener('input', () => this._onInput());
      if (this.clearBtn) this.clearBtn.addEventListener('click', (e) => { e.preventDefault(); this._clear(); });

      // Submit on Enter — let the form go to /search natively.

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      // Tag clicks: pre-fill the input + run the live search instead of navigating.
      this.querySelectorAll('[data-pb-search-tag]').forEach((tag) => {
        tag.addEventListener('click', (e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          if (!this.input) return;
          e.preventDefault();
          this.input.value = tag.dataset.pbSearchTag || tag.textContent.trim();
          this.input.focus();
          this._onInput(true);
        });
      });
    }

    open() {
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.body.classList.add('pb-search-open');
      });
      this.lastFocus = document.activeElement;
      setTimeout(() => { if (this.input) this.input.focus(); }, 160);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pb-search-open');
      setTimeout(() => { this.setAttribute('hidden', ''); }, 420);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus({ preventScroll: true });
      }
    }

    toggle() {
      this.classList.contains('is-open') ? this.close() : this.open();
    }

    _clear() {
      if (!this.input) return;
      this.input.value = '';
      this.input.focus();
      this._onInput();
    }

    _onInput(immediate) {
      if (!this.input) return;
      var q = this.input.value.trim();

      if (this.clearBtn) {
        if (q.length > 0) this.clearBtn.removeAttribute('hidden');
        else this.clearBtn.setAttribute('hidden', '');
      }

      // Empty query → restore featured products
      if (q.length === 0) {
        if (this.fetchTimer) { clearTimeout(this.fetchTimer); this.fetchTimer = null; }
        if (this.abortCtl) { try { this.abortCtl.abort(); } catch (_) {} this.abortCtl = null; }
        if (this.productsEl) this.productsEl.innerHTML = this.featuredHtml;
        if (this.noResults) this.noResults.setAttribute('hidden', '');
        this.lastQuery = '';
        return;
      }

      if (q === this.lastQuery && !immediate) return;
      this.lastQuery = q;

      if (this.fetchTimer) clearTimeout(this.fetchTimer);
      this.fetchTimer = setTimeout(() => this._search(q), immediate ? 0 : DEBOUNCE_MS);
    }

    _search(query) {
      if (!query || !this.productsEl) return;

      // Cancel in-flight request
      if (this.abortCtl) { try { this.abortCtl.abort(); } catch (_) {} }
      this.abortCtl = ('AbortController' in window) ? new AbortController() : null;

      var params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': FETCH_LIMIT,
        'resources[options][unavailable_products]': 'last'
        // PB:MODIFIED 2026-06-08 (QA S-01) — dropped the restrictive
        // `fields` option. It limited which fields searched/returned, which
        // suppressed featured_image in the response → blank thumbnails.
      });

      var fetchOpts = this.abortCtl ? { signal: this.abortCtl.signal } : {};

      fetch(SUGGEST_URL + '?' + params.toString(), fetchOpts)
        .then((r) => r.json())
        .then((data) => {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          this._renderResults(products);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          // Fall back: keep the featured products visible
          if (this.productsEl) this.productsEl.innerHTML = this.featuredHtml;
        });
    }

    _renderResults(products) {
      if (!this.productsEl) return;

      if (!products || products.length === 0) {
        this.productsEl.innerHTML = '';
        if (this.noResults) this.noResults.removeAttribute('hidden');
        return;
      }
      if (this.noResults) this.noResults.setAttribute('hidden', '');

      var html = products.map((p) => this._cardHtml(p)).join('');
      this.productsEl.innerHTML = html;
    }

    _cardHtml(p) {
      var url = escapeHtml(p.url || '#');
      var title = escapeHtml(p.title || '');
      // suggest.json may expose the thumbnail as `image` (string),
      // `featured_image` (string URL), or `featured_image.url` (object).
      var fi = p.featured_image;
      var img = p.image
        || (typeof fi === 'string' ? fi : (fi && fi.url))
        || '';
      var imgUrl = img ? escapeHtml(img) : '';
      // suggest.json returns price as a dollar string with no symbol;
      // formatPrice() normalises it (and numeric cents) to "$xx.xx".
      var price = escapeHtml(formatPrice(p.price));

      var compareAt = formatPrice(p.compare_at_price);
      var compareHtml = '';
      if (compareAt) {
        compareHtml = '<s>' + escapeHtml(compareAt) + '</s> ';
      }

      var imgHtml = imgUrl
        ? '<img src="' + imgUrl + '" alt="' + title + '" loading="lazy" width="56" height="56">'
        : '<div class="pb-placeholder-svg" aria-hidden="true"></div>';

      return (
        '<a href="' + url + '" class="pb-search__product" data-product-title="' + title.toLowerCase() + '">' +
          '<div class="pb-search__product-img">' + imgHtml + '</div>' +
          '<div class="pb-search__product-info">' +
            '<h4 class="pb-search__product-title">' + title + '</h4>' +
            '<span class="pb-search__product-price">' + compareHtml + price + '</span>' +
          '</div>' +
        '</a>'
      );
    }
  }

  customElements.define('pb-search-drawer', PbSearchDrawer);

  /* ---------- Wire up the header search trigger ---------- */
  function wireTriggers() {
    var drawer = document.getElementById('PbSearchDrawer');
    if (!drawer) return;

    var triggers = document.querySelectorAll(
      '.header__icon--search, ' +
      'summary-search > summary, ' +
      'details-modal > summary[aria-label*="earch"], ' +
      'a[href="/search"], ' +
      '[data-pb-open-search]'
    );

    triggers.forEach((el) => {
      if (el.dataset.pbSearchBound === '1') return;
      el.dataset.pbSearchBound = '1';
      el.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || (e.button && e.button !== 0)) return;
        e.preventDefault();
        e.stopPropagation();
        if (el.tagName && el.tagName.toLowerCase() === 'summary') {
          var details = el.closest('details');
          if (details && details.hasAttribute('open')) details.removeAttribute('open');
        }
        drawer.open();
      }, true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireTriggers);
  } else {
    wireTriggers();
  }
  document.addEventListener('shopify:section:load', wireTriggers);
})();
